import classnames from 'classnames';
import _ from 'lodash';
import React, {Component} from 'react';
import CircularProgressbar from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {connect} from 'react-redux';
import {
  closeGraphsView,
  ejectGraphsView,
  printGraphsView,
  resetAllManualWeatherData,
  selectDataSet,
  setActiveTime,
  setShowWeather,
  setTableBackground,
  setTableDirections,
  toggleGraphsDays,
  toggleGraphsExpanded,
  toggleGraphsHistory,
  togglePointDataDisplay,
  toggleShowAllCalcColumns
} from '../actions';
import ActiveIndicatorMessage from '../components/ActiveIndicatorMessage';
import ExportForm from '../components/ExportForm';
import AreaChartGraph from '../components/graphs/AreaChartGraph';
import LineChartGraph from '../components/graphs/LineChartGraph';
import ManualWeatherForm from '../components/ManualWeatherForm';
import MessageBox from '../components/MessageBox';
import {AEROLOGICAL_WS, DATA_SETS, MODES, NO_AWS_NOTES, TABLE_BACKGROUNDS, TABLE_DIRECTIONS} from '../Constants';
import {updateTimeOrchestrator} from '../orchestrators';
import {shouldCheckIndicatorAge} from '../utils/indicator_utils';
import {buildIspTableData, getIspColumnMetadata} from '../utils/isp_dataset';
import {PREFIX_RH, PREFIX_T} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {
  applyLagTempRhShift,
  displayModificationsForLag,
  isLagTempRhGap,
  lagHoursForDataSet,
  lagSourceRow
} from '../utils/lag_weather_utils';
import {flooredHour, formatForGraph, isTimeOver12hrs} from '../utils/time_utils';
import {toCardinal} from '../utils/wind_utils';
import GrassVariables from './GrassVariables';
import Leaflet80Variables from './Leaflet80Variables';
import MalleeHeathVariables from './MalleeHeathVariables';
import McArthurMk5Variables from './McArthurMk5Variables';
import './PointGraphs.css';
import PrescriptionsVariables from './PrescriptionsVariables';
import VestaMk2Variables from './VestaMk2Variables';
import VestaVariables from './VestaVariables';

class PointGraphs extends Component {

  constructor(props) {
    super(props);
    this.state = { table_row_click: false };
  }

  onClose = () => {
    this.props.dispatch(closeGraphsView());
  };

  onToggleGraphsExpanded = () => {
    this.props.dispatch(toggleGraphsExpanded());
  };

  onToggleHistory = () => {
    this.props.dispatch(toggleGraphsHistory());
  };

  onToggleGraphDays = () => {
    this.props.dispatch(toggleGraphsDays());
  };

  onSetTableBackground = (table_background) => {
    this.props.dispatch(setTableBackground(table_background));
  };

  onSetShowWeather = (show_weather) => {
    this.props.dispatch(setShowWeather(show_weather));
  };

  onSetTableDirections = (table_directions) => {
    this.props.dispatch(setTableDirections(table_directions));
  };

  onToggleShowAllCalcColumns = () => {
    this.props.dispatch(toggleShowAllCalcColumns());
  };

  onToggleDisplay = () => {
    this.props.dispatch(togglePointDataDisplay());
  };

  onSelectDataSet = (data_set) => {
    this.props.dispatch(selectDataSet(data_set));
  };

  onGraphClick = (data, e) => {
    const data_point = _.find(data, d => e && d.name === e.activeLabel);
    if (data_point) {
      this.props.dispatch(updateTimeOrchestrator(flooredHour(data_point.sortIndex)));
    }
  };

  onEjectGraphsView = () => {
    this.props.dispatch(ejectGraphsView(false));
  };

  onPrintGraphs = () => {
    this.props.dispatch(printGraphsView(true));
  };

  onClickTableRow = (e, time, enable_edit) => {
    this.setState({ table_row_click: true });
    this.props.dispatch(setActiveTime(time));

    // Detect double click and click on the edit-action
    if (e.detail === 2 && enable_edit) {
      // Make sure supported by browser
      if (e.target.closest) {
        const row = e.target.closest('tr');
        if (row) {
          row.querySelector('.edit-action').click();
        }
      }
    }
  };

  onResetModifications = () => {
    this.props.dispatch(resetAllManualWeatherData());
  };

  indicatorDataIsOld = (ind) => {
    return shouldCheckIndicatorAge(ind) && isTimeOver12hrs(ind.creationTime);
  };

  indicatorName = (ind) => {
    const name = ind.tableDisplayName || ind.displayName;
    const bracketIndex = name.indexOf('(');
    return bracketIndex === -1 ? name : name.slice(0, bracketIndex).trim();
  };

  indicatorUnits = (ind) => {
    const name = ind.tableDisplayName || ind.displayName;
    const bracketIndex = name.indexOf('(');
    return bracketIndex === -1 ? '' : name.slice(bracketIndex);
  };

  componentDidUpdate(prevProps) {
    if (this.props.time.selected_time !== prevProps.time.selected_time) {
      if (this.state.table_row_click) {
        this.setState({ table_row_click: false });
      }
    }
  }

  render() {
    // trailWidth="4" strokeWidth="4" trailColor="#D3D3D3"  strokeColor="#009E67"
    const {
      graphs_open,
      graphs_loaded,
      graph_view_expanded,
      pop_out,
      include_history,
      short_future,
      point_data_display_graph,
      active_indicator,
      time
    } = this.props;
    if (graphs_open) {
      return <div className={classnames('c-graphs__container', {
        'c-graphs__open': graphs_open,
        'c-graphs__disp-graph': point_data_display_graph,
        'c-graphs__disp-table': !point_data_display_graph,
        'c-graphs__expanded': graph_view_expanded,
        'pop_out': pop_out
      })}>
        <div className="c-graphs__buttons">
          <div className="button-group">
            <label>History:</label>
            <div className="buttons">
              <div className={'c-button' + (include_history ? ' selected' : '')}
                   onClick={include_history ? null : this.onToggleHistory}
                   title="Show data for 2 days in the past">Show
              </div>
              <div className={'c-button' + (include_history ? '' : ' selected')}
                   onClick={include_history ? this.onToggleHistory : null}
                   title="Do not show data from the past">Hide
              </div>
            </div>
          </div>
          <div className="button-group" style={{ marginLeft: '1em' }}>
            <label>Days:</label>
            <div className="buttons">
              <div className={'c-button' + (short_future ? ' selected' : '')}
                   onClick={short_future ? null : this.onToggleGraphDays}
                   title="Show data for 2 days into the future">2
              </div>
              <div className={'c-button' + (!short_future ? ' selected' : '')}
                   onClick={short_future ? this.onToggleGraphDays : null}
                   title="Show data for 7 days into the future">7
              </div>
            </div>
          </div>
          <div className="button-group" style={{ marginLeft: '1em' }}>
            <label>Display:</label>
            <div className="buttons">
              <div className={'c-button' + (point_data_display_graph ? ' selected' : '')}
                   onClick={point_data_display_graph ? null : this.onToggleDisplay}
                   title="Display data in graphs">Graphs
              </div>
              <div className={'c-button' + (!point_data_display_graph ? ' selected' : '')}
                   onClick={point_data_display_graph ? this.onToggleDisplay : null}
                   title="Display data in tabular form">Table
              </div>
            </div>
          </div>
          <div className="flex-spacer"/>
          <div className="button-group graph-panel-actions">
            {pop_out &&
             <div className="buttons">
               <div className="c-button" onClick={window.close} title="Close meteograms">
                 <i className="fa fa-times-circle"/>
                 <label>Close</label>
               </div>
             </div>
            }
            {!pop_out &&
             <div className="buttons">
               {point_data_display_graph &&
                <div className="c-button c-button__expand" onClick={this.onToggleGraphsExpanded}
                     title={(graph_view_expanded ? 'Compress' : 'Expand') +
                            (point_data_display_graph ? ' meteograms' : ' table')}>
                  <i className={graph_view_expanded ? 'fa fa-compress' : 'fa fa-expand'}/>
                  <label>{graph_view_expanded ? 'Compress' : 'Expand'}</label>
                </div>
               }
               <div className="c-button" onClick={this.onEjectGraphsView}
                    title="Open meteograms in a new window">
                 <i className="fa fa-external-link"/>
                 <label>Open</label>
               </div>
               <div className="c-button" onClick={this.onPrintGraphs}
                    title="Print preview">
                 <i className="fa fa-print"/>
                 <label>Print</label>
               </div>
               <div className="c-button" onClick={this.onClose}
                    title="Close meteograms">
                 <i className="fa fa-times-circle"/>
                 <label>Close</label>
               </div>
             </div>
            }
          </div>
        </div>
        {this.renderLocationDetails()}
        {graphs_loaded && this.renderDataSetSelector()}
        {graphs_loaded && this.renderDataSetVariablesPanel()}
        {!graphs_loaded && this.renderProgress()}
        {graphs_loaded && this.renderOutOfDateWarning(point_data_display_graph)}
        {(graphs_loaded && point_data_display_graph) && this.renderGraphs()}
        {(graphs_loaded && !point_data_display_graph) && this.renderTable()}
        {graph_view_expanded && !pop_out &&
         <div className="print-only"><ActiveIndicatorMessage active_indicator={active_indicator} time={time}/></div>}
      </div>;
    }
    return null;
  }

  renderLocationDetails() {
    const { point, aws, fms, manual_weather } = this.props;
    let point_details;
    if (point && point.lat) {
      point_details = <div className="location_data">
        <div>
          <span className="c-form__label_readonly">Lat:</span>
          <span className="c-form__value_readonly">{point.lat ? point.lat.toFixed(4) : 'Not set'}</span>
          &nbsp;&nbsp;&nbsp;
          <span className="c-form__label_readonly">Long:</span>
          <span className="c-form__value_readonly">{point.lng ? +point.lng.toFixed(4) : 'Not set'}</span>
        </div>
        <div>
          <span className="c-form__label_readonly">MGRS:</span>
          <span className="c-form__value_readonly">{point.mgrs}</span>
        </div>
        <div>
          <span className="c-form__label_readonly">Phoenix Fuel Type:</span>
          <span className="c-form__value_readonly">{point.fuel_type}</span>
        </div>
      </div>;
    }
    let aws_details;
    if (aws) {
      aws_details =
        <div className="aws_data">
          <div className="emphasis">Nearest WS:</div>
          <div className="station">
            <a href={aws.awsExternalUrl} target="bom_ws">
              <i className="fa fa-external-link"/>
              {aws.description} ({aws.distanceToPoint} km)
            </a>
            {AEROLOGICAL_WS.includes(aws.stationid) &&
             <div>
               <a href={aws.aerologicaExternallUrl} target="bom_ws_aero">
                 <i className="far fa-chart-line"/>Aerological Diagram
               </a>
             </div>}
          </div>
        </div>;
    }
    let fms_details;
    if (fms) {
      fms_details =
        <div className="fms_data">
          <div className="emphasis">Nearest FMS:</div>
          <div className="station">
            <a href={fms.externalUrl} target="fms">
              <i className="fa fa-external-link"/>
              {fms.name} ({fms.distanceToPoint} km)
            </a>
          </div>
        </div>;
    }
    let manual_weather_warning;
    if (manual_weather) {
      manual_weather_warning =
        <div className="manual_weather_warning">
          <span className="warning">NOTE:</span>
          The selected point includes manually adjusted weather data.
          <span className="c-button reset-button" onClick={this.onResetModifications}><i className="fa fa-undo"/>Reset All</span>
        </div>;
    }
    return <div className="c-graphs__details">{point_details}{aws_details}{fms_details}{manual_weather_warning}</div>;
  }

  renderOutOfDateWarning(onGraphs) {
    const { weather_data, app_mode } = this.props;
    const weather_column_meta_data = weather_data.table_meta_data
                                                 .filter(i => !i.hide_in_table)
                                                 .filter(i => !i.only_in_app_mode || i.only_in_app_mode === app_mode);

    if (weather_column_meta_data && weather_column_meta_data.some(ind => this.indicatorDataIsOld(ind))) {
      if (onGraphs) {
        return <div className="c-graphs__outofdate_warning">
          Some weather data is out of date. Please refer to the data tables for details.
        </div>;
      } else {
        return <div className="c-graphs__outofdate_warning">
          Columns marked with the <b>[ ! ]</b> icon are displaying data that is over 12 hours out-of-date.
        </div>;
      }
    }
  }

  renderDataSetSelector() {
    const { data_set, app_mode } = this.props;

    return <div className="c-graphs__buttons">
      <div className="button-group">
        <label>Data Set:</label>
        <div className="buttons">
          <div className={'c-button' + (data_set === DATA_SETS.WEATHER ? ' selected' : '')}
               onClick={this.onSelectDataSet.bind(null, DATA_SETS.WEATHER)}
               title="Weather">{DATA_SETS.WEATHER}
          </div>
          <div className={'c-button' + (data_set === DATA_SETS.ISP ? ' selected' : '')}
               onClick={this.onSelectDataSet.bind(null, DATA_SETS.ISP)}
               title="ISP reference (curated weather and fire-behaviour columns)">{DATA_SETS.ISP}
          </div>
          {<div className={'c-button' + (data_set === DATA_SETS.MCARTHUR_MK5 ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.MCARTHUR_MK5)}
                title="McArthur Mk5">{DATA_SETS.MCARTHUR_MK5}
          </div>
          }
          {<div className={'c-button' + (data_set === DATA_SETS.LEAFLET_80 ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.LEAFLET_80)}
                title="Leaflet 80">{DATA_SETS.LEAFLET_80}
          </div>
          }
          {app_mode === MODES.WILDFIRE &&
           <div className={'c-button' + (data_set === DATA_SETS.VESTA ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.VESTA)}
                title="VESTA">{DATA_SETS.VESTA}
           </div>
          }
          {app_mode === MODES.WILDFIRE &&
           <div className={'c-button' + (data_set === DATA_SETS.VESTA_MK2_DRY ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.VESTA_MK2_DRY)}
                title="Dry eucalypt phase model; requires BoM drought factor (HP_DF_SFC)">{DATA_SETS.VESTA_MK2_DRY}
           </div>
          }
          {app_mode === MODES.WILDFIRE &&
           <div className={'c-button' + (data_set === DATA_SETS.VESTA_MK2_WET ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.VESTA_MK2_WET)}
                title="Wet eucalypt phase model; requires drought factor and KBDI from weather">{DATA_SETS.VESTA_MK2_WET}
           </div>
          }
          {<div className={'c-button' + (data_set === DATA_SETS.GRASS ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.GRASS)}
                title="Grass">{DATA_SETS.GRASS}
          </div>
          }
          {app_mode === MODES.WILDFIRE &&
           <div className={'c-button' + (data_set === DATA_SETS.MALLEE_HEATH ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.MALLEE_HEATH)}
                title="Mallee Heath">{DATA_SETS.MALLEE_HEATH}
           </div>
          }
          {app_mode === MODES.PLANNED_BURNS &&
           <div className={'c-button' + (data_set === DATA_SETS.PRESCRIPTIONS ? ' selected' : '')}
                onClick={this.onSelectDataSet.bind(null, DATA_SETS.PRESCRIPTIONS)}
                title="Prescriptions">{DATA_SETS.PRESCRIPTIONS}
           </div>
          }

        </div>
      </div>
      <ExportForm dispatch={this.props.dispatch}/>
    </div>;
  }

  renderDataSetVariablesPanel() {
    const { data_set } = this.props;
    switch (data_set) {
      case DATA_SETS.MCARTHUR_MK5:
        return <McArthurMk5Variables/>;
      case DATA_SETS.VESTA:
        return <VestaVariables/>;
      case DATA_SETS.VESTA_MK2_DRY:
      case DATA_SETS.VESTA_MK2_WET:
        return <VestaMk2Variables/>;
      case DATA_SETS.LEAFLET_80:
        return <Leaflet80Variables/>;
      case DATA_SETS.GRASS:
        return <GrassVariables/>;
      case DATA_SETS.MALLEE_HEATH:
        return <MalleeHeathVariables/>;
      case DATA_SETS.PRESCRIPTIONS:
        return <PrescriptionsVariables/>;
      default:
        return null;
    }
  }

  // dotDensity only affects series with a dotType.
  // If set, it specifies which dots should be displayed. 1 = all, 2 = every second etc.
  calcDotDensity() {
    const { graph_view_expanded, include_history, short_future } = this.props;

    let density = 8;
    if (graph_view_expanded) {
      density /= 4.0;
    }
    if (short_future) {
      density /= 2.0;
    }
    if (!include_history) {
      density /= 1.5;
    }
    return Math.ceil(density);
  }

  renderGraphs() {
    const {
      ticks,
      weather_data,
      mcarthur_mk5_data,
      vesta_data,
      vesta_mk2_dry_data,
      vesta_mk2_wet_data,
      leaflet_80_data,
      grass_data,
      mallee_heath_data,
      prescriptions_data,
      data_set,
      time,
      aws,
      app_mode
    } = this.props;
    const dotDensity = this.calcDotDensity();
    const now_x = formatForGraph(flooredHour(new Date()));
    const selected_time_x = formatForGraph(time.selected_time);

    let graph_data;
    let meta_data;
    switch (data_set) {
      case DATA_SETS.MCARTHUR_MK5:
        graph_data = mcarthur_mk5_data.graph_data;
        meta_data = mcarthur_mk5_data.graph_meta_data;
        break;
      case DATA_SETS.VESTA:
        graph_data = vesta_data.graph_data;
        meta_data = vesta_data.graph_meta_data;
        break;
      case DATA_SETS.VESTA_MK2_DRY:
        graph_data = vesta_mk2_dry_data.graph_data;
        meta_data = vesta_mk2_dry_data.graph_meta_data;
        break;
      case DATA_SETS.VESTA_MK2_WET:
        graph_data = vesta_mk2_wet_data.graph_data;
        meta_data = vesta_mk2_wet_data.graph_meta_data;
        break;
      case DATA_SETS.LEAFLET_80:
        graph_data = leaflet_80_data.graph_data;
        meta_data = leaflet_80_data.graph_meta_data;
        break;
      case DATA_SETS.GRASS:
        graph_data = grass_data.graph_data;
        meta_data = grass_data.graph_meta_data;
        break;
      case DATA_SETS.MALLEE_HEATH:
        graph_data = mallee_heath_data.graph_data;
        meta_data = mallee_heath_data.graph_meta_data;
        break;
      case DATA_SETS.PRESCRIPTIONS:
        graph_data = { ...prescriptions_data.graph_data, ...weather_data.graph_data, ...leaflet_80_data.graph_data };
        meta_data = prescriptions_data.graph_meta_data;
        break;
      case DATA_SETS.ISP:
        graph_data = weather_data.graph_data;
        meta_data = weather_data.graph_meta_data;
        break;
      default:
        graph_data = weather_data.graph_data;
        meta_data = weather_data.graph_meta_data;
    }

    return (<div className="c-graphs__graphs">
      {!aws && <MessageBox messages={[NO_AWS_NOTES]} warning={true} dangerouslySetInnerHTML={true}/>}
      {meta_data.map((graph, i) => {
        const data = graph_data[graph.id];
        if (!graph.hide &&
            (!graph.only_in_app_mode || graph.only_in_app_mode === app_mode) &&
            data && data.length > 0) {
          return (
            <div className="c-graphs__graph" key={i}>
              <h4>{graph.title}</h4>
              {graph.type === 'LineChart' &&
               <LineChartGraph data={graph_data[graph.id]}
                               ticks={ticks}
                               graph={graph}
                               dotDensity={dotDensity}
                               now_x={now_x}
                               selected_time_x={selected_time_x}
                               onGraphClick={this.onGraphClick}/>
              }
              {graph.type === 'AreaChart' &&
               <AreaChartGraph data={graph_data[graph.id]}
                               ticks={ticks}
                               graph={graph}
                               now_x={now_x}
                               selected_time_x={selected_time_x}
                               onGraphClick={this.onGraphClick}/>
              }
            </div>
          );
        } else {
          return null;
        }
      })}
    </div>);
  }

  scrollIntoView = (className) => {
    const container = this.refs.table_container;
    if (container) {
      const elements = container.getElementsByClassName(className);
      if (elements && elements.length > 0) {
        const offset = elements[0].offsetTop;
        container.scrollTop = offset - 280;
      }
    }
  };

  renderTable() {
    const {
      data_set,
      weather_data,
      mcarthur_mk5_data,
      vesta_data,
      vesta_mk2_dry_data,
      vesta_mk2_wet_data,
      leaflet_80_data,
      grass_data,
      mallee_heath_data,
      prescriptions_data,
      time,
      manual_weather,
      show_weather,
      table_background,
      table_directions,
      show_all_calc_columns,
      app_mode
    } = this.props;
    const now = formatForGraph(flooredHour(new Date()));
    const formatted_selected_time = formatForGraph(time.selected_time);
    const show_selected_time = now !== formatted_selected_time;

    const weather_modifications = manual_weather ? manual_weather.properties.data : {};
    const lagHours = lagHoursForDataSet({
      point_data_mcarthur_mk5: mcarthur_mk5_data,
      point_data_leaflet_80: leaflet_80_data,
      point_data_vesta: vesta_data,
      point_data_vesta_mk2_dry: vesta_mk2_dry_data,
      point_data_vesta_mk2_wet: vesta_mk2_wet_data,
      point_data_mallee_heath: mallee_heath_data
    }, data_set);
    const weather_table_data = applyLagTempRhShift(
      weather_data.table_data,
      weather_modifications,
      lagHours
    );
    const weather_baseline_data = lagHours ?
      applyLagTempRhShift(weather_data.table_data, {}, lagHours) :
      weather_data.table_data;
    const weather_display_modifications = displayModificationsForLag(
      weather_data.table_data,
      weather_modifications,
      lagHours
    );
    const first_weather_row = this.firstWeatherRow(weather_table_data);
    const weather_column_meta_data = weather_data.table_meta_data
                                                 .filter(i => !i.hide_in_table)
                                                 .filter(i => !i.only_in_app_mode || i.only_in_app_mode === app_mode);

    let table_data;
    let column_meta_data;
    let modifications;
    let dataset_is_weather = false;
    switch (data_set) {
      case DATA_SETS.MCARTHUR_MK5:
        table_data = mcarthur_mk5_data.table_data;
        column_meta_data = mcarthur_mk5_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.VESTA:
        table_data = vesta_data.table_data;
        column_meta_data = vesta_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.VESTA_MK2_DRY:
        table_data = vesta_mk2_dry_data.table_data;
        column_meta_data = vesta_mk2_dry_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.VESTA_MK2_WET:
        table_data = vesta_mk2_wet_data.table_data;
        column_meta_data = vesta_mk2_wet_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.LEAFLET_80:
        table_data = leaflet_80_data.table_data;
        column_meta_data = leaflet_80_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.GRASS:
        table_data = grass_data.table_data;
        column_meta_data = grass_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.MALLEE_HEATH:
        table_data = mallee_heath_data.table_data;
        column_meta_data = mallee_heath_data.table_meta_data;
        modifications = {};
        break;
      case DATA_SETS.PRESCRIPTIONS:
        table_data = prescriptions_data.table_data;
        column_meta_data = prescriptions_data.table_meta_data.filter(i => !i.hide_in_table);
        modifications = {};
        break;
      case DATA_SETS.ISP:
        table_data = buildIspTableData(weather_table_data, mcarthur_mk5_data.table_data, grass_data.table_data);
        column_meta_data = getIspColumnMetadata(weather_data.table_meta_data, grass_data.table_meta_data);
        modifications = weather_modifications;
        break;
      default:
        table_data = weather_table_data;
        column_meta_data = weather_column_meta_data;
        modifications = weather_modifications;
        dataset_is_weather = true;
    }

    const table_rows = _.orderBy(_.values(table_data), 'sortIndex');
    // ISP is a single merged table (weather columns are already inside column_meta_data); do not add the extra Weather block used for McArthur/Grass + “Show weather”.
    const isp_table = data_set === DATA_SETS.ISP;
    const include_weather_columns = !dataset_is_weather && show_weather && !isp_table;
    const enable_edit = dataset_is_weather || include_weather_columns || isp_table;
    const data_has_calc_columns = column_meta_data.some(e => e.nonFinal);
    // At the moment weather is always displayed, so there are always some arrows.
    const data_has_arrow_columns = true;

    return (
      <div className="fixed-header-table-container" ref="table_container">
        <table className="c-graphs__table">
          <thead>
          <tr>
            <th className="table-controls" rowSpan={2}>
              <div>
                <div className="time-options">
                  {!dataset_is_weather && !isp_table &&
                   <div className="button-group">
                     <label>Weather:</label>
                     <div className="buttons">
                       <div className={'c-button' + (show_weather ? ' selected' : '')}
                            onClick={() => this.onSetShowWeather(true)}>Show
                       </div>
                       <div className={'c-button' + (!show_weather ? ' selected' : '')}
                            onClick={() => this.onSetShowWeather(false)}>Hide
                       </div>
                     </div>
                   </div>
                  }
                  <div className="button-group">
                    <label>Backgrounds:</label>
                    <div className="buttons">
                      <div className={'c-button' + (table_background === TABLE_BACKGROUNDS.NONE ? ' selected' : '')}
                           onClick={() => this.onSetTableBackground(TABLE_BACKGROUNDS.NONE)}>None
                      </div>
                      <div className={'c-button' + (table_background === TABLE_BACKGROUNDS.COLOR ? ' selected' : '')}
                           onClick={() => this.onSetTableBackground(TABLE_BACKGROUNDS.COLOR)}>Colour
                      </div>
                      <div className={'c-button' + (table_background === TABLE_BACKGROUNDS.GRAPH ? ' selected' : '')}
                           onClick={() => this.onSetTableBackground(TABLE_BACKGROUNDS.GRAPH)}>Graph
                      </div>
                    </div>
                  </div>
                  {data_has_calc_columns &&
                   <div className="button-group">
                     <label>Values:</label>
                     <div className="buttons">
                       <div className={'c-button' + (show_all_calc_columns ? ' selected' : '')}
                            onClick={show_all_calc_columns ? null : this.onToggleShowAllCalcColumns}>All
                       </div>
                       <div className={'c-button' + (show_all_calc_columns ? '' : ' selected')}
                            onClick={show_all_calc_columns ? this.onToggleShowAllCalcColumns : null}>Final
                       </div>
                     </div>
                   </div>
                  }
                  {data_has_arrow_columns &&
                   <div className="button-group">
                     <label>Directions:</label>
                     <div className="buttons">
                       <div className={'c-button' + (table_directions === TABLE_DIRECTIONS.ARROWS ? ' selected' : '')}
                            onClick={() => this.onSetTableDirections(TABLE_DIRECTIONS.ARROWS)}>Arrows
                       </div>
                       <div className={'c-button' +
                                       (table_directions === TABLE_DIRECTIONS.CARDINALS ? ' selected' : '')}
                            onClick={() => this.onSetTableDirections(TABLE_DIRECTIONS.CARDINALS)}>Cardinals
                       </div>
                       <div className={'c-button' + (table_directions === TABLE_DIRECTIONS.NUMBERS ? ' selected' : '')}
                            onClick={() => this.onSetTableDirections(TABLE_DIRECTIONS.NUMBERS)}>Degrees
                       </div>
                     </div>
                   </div>
                  }
                </div>

                <div className="time-buttons">
                  <div className="now">
                    <div className="label">Now</div>
                    <div className="c-button light" onClick={() => this.scrollIntoView('now-row')}>Go</div>
                  </div>
                  {show_selected_time &&
                   <div className="selected-time">
                     <div>
                       <div className="label">Selected time</div>
                       <div className="formatted-time">{formatted_selected_time}</div>
                     </div>
                     <div className="c-button light"
                          onClick={() => this.scrollIntoView('selected-time-row')}>
                       Go
                     </div>
                   </div>}
                </div>
                <div>Time</div>
              </div>
            </th>
            {include_weather_columns &&
             <th colSpan={weather_column_meta_data.length} className="cross-col-header"
                 style={this.thStyle(weather_column_meta_data[0])}>{DATA_SETS.WEATHER}</th>}
            <th colSpan={column_meta_data.length} className="cross-col-header"
                style={this.thStyle(column_meta_data[0])}>{data_set}</th>
          </tr>
          <tr className="indicator-headers">
            {include_weather_columns &&
             weather_column_meta_data.map(ind => <th key={ind.key} style={this.thStyle(ind)}>
               <div>
                 <span>
                   {this.indicatorDataIsOld(ind) &&
                    <span className="too-old" title="The data in this column is older than 12 hours.">!</span>}
                   <span className="th-indicator-name">
                     <span>{this.indicatorName(ind)}</span>
                     <span>{this.indicatorUnits(ind)}</span>
                   </span>
                 </span>
                 {enable_edit && ind.column_editable && <ManualWeatherForm data={first_weather_row}
                                                                           modifications={first_weather_row
                                                                                          ?
                                                                                          weather_display_modifications[first_weather_row.name]
                                                                                          :
                                                                                          null}
                                                                           storageRow={first_weather_row
                                                                                       ?
                                                                                       lagSourceRow(
                                                                                         weather_data.table_data,
                                                                                         first_weather_row,
                                                                                         lagHours
                                                                                       )
                                                                                       :
                                                                                       null}
                                                                           single_variable={ind.key}
                                                                           meta_data={weather_column_meta_data}
                                                                           dispatch={this.props.dispatch}/>}
               </div>
             </th>)}
            {column_meta_data.filter(ind => show_all_calc_columns || !ind.nonFinal)
                             .map(ind => <th key={ind.key} style={this.thStyle(ind)}>
                               <div>
                                 <span>
                                   {this.indicatorDataIsOld(ind) &&
                                    <span className="too-old"
                                          title="The data in this column is older than 12 hours.">!</span>}
                                   <span className="th-indicator-name">
                                     <span>{this.indicatorName(ind)}</span>
                                     <span>{this.indicatorUnits(ind)}</span>
                                   </span>
                                 </span>
                                 {enable_edit && ind.column_editable && <ManualWeatherForm data={first_weather_row}
                                                                                           modifications={first_weather_row
                                                                                                          ?
                                                                                                          weather_display_modifications[first_weather_row.name]
                                                                                                          :
                                                                                                          null}
                                                                                           storageRow={first_weather_row
                                                                                                       ?
                                                                                                       lagSourceRow(
                                                                                                         weather_data.table_data,
                                                                                                         first_weather_row,
                                                                                                         lagHours
                                                                                                       )
                                                                                                       :
                                                                                                       null}
                                                                                           single_variable={ind.key}
                                                                                           meta_data={weather_column_meta_data}
                                                                                           dispatch={this.props.dispatch}/>}
                               </div>
                             </th>)}
          </tr>
          </thead>
          <tbody>
          {table_rows.map((data) => {
            return <tr key={data.name}
                       onClick={(e) => this.onClickTableRow(e, data.sortIndex, enable_edit)}
                       className={classnames(now === data.name ? 'now-row' : '',
                         show_selected_time && formatted_selected_time === data.name ? 'selected-time-row' : '')}>
              <td className="time-column">
                <span>{data.name}</span>
                {enable_edit && <ManualWeatherForm data={weather_table_data[data.name]}
                                                   modifications={weather_display_modifications[data.name]}
                                                   storageRow={lagSourceRow(
                                                     weather_data.table_data,
                                                     weather_data.table_data[data.name],
                                                     lagHours
                                                   )}
                                                   meta_data={weather_column_meta_data}
                                                   dispatch={this.props.dispatch}/>}
              </td>
              {include_weather_columns &&
               weather_column_meta_data.map(
                 metadata => this.dataCell(metadata, weather_table_data[data.name],
                   weather_display_modifications[data.name], table_background, table_directions,
                   lagHours, weather_baseline_data[data.name],
                   isLagTempRhGap(weather_data.table_data, weather_data.table_data[data.name], lagHours) &&
                   (metadata.key === PREFIX_T || metadata.key === PREFIX_RH)))}
              {column_meta_data.filter(ind => show_all_calc_columns || !ind.nonFinal)
                               .map(metadata => this.dataCell(metadata,
                                 data,
                                 modifications[data.name],
                                 table_background,
                                 table_directions))}
            </tr>;
          })}
          </tbody>
        </table>
      </div>);
  }

  renderProgress() {
    const { progress_percentage, progress_seconds, pop_out, print_on_load } = this.props;
    const message = print_on_load ? 'Print will commence when data is loaded' : 'Data is loading';

    return (<div className="c-graphs__graphs">
      <div className="c-graphs__loading_message">
        <div className="c-graphs__loading_message__text">{message}... {progress_seconds}s</div>
        <CircularProgressbar percentage={progress_percentage} text={`${progress_percentage}%`}
                             background
                             backgroundPadding={6}
                             styles={{
                               background: {
                                 fill: `#9CD0EC`
                               },
                               text: {
                                 fill: '#FFF'
                               },
                               path: {
                                 stroke: '#FF953D'
                               },
                               trail: { stroke: 'transparent' }
                             }}
        />
        {!pop_out &&
         <div className="button-panel">
           <a className={'c-button light'} onClick={() => this.onClose()}>
             <i className="fa fa-times-circle"/> Cancel! </a>
         </div>
        }
      </div>
    </div>);
  }

  dataCell(metadata, data, modifications, table_background, table_directions, lagHours = 0, baselineData, lagGap = false) {
    const { key, decPlaces, dataSet, colors, cellGraph, arrow, formatFn } = metadata;
    const defaultFormater = (value) =>
      typeof value !== 'undefined' && value !== null ?
      value.toFixed(decPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',') :
      '';
    const formatNumber = formatFn || defaultFormater;

    let title = '';
    let style = {};
    let classname = '';
    if (dataSet === DATA_SETS.WEATHER) {
      classname += ' weather';
    }

    if (!data) {
      return <td key={'td_' + key} className={classname.trim()} title={title} style={style}/>;
    }

    if (lagGap) {
      title = 'No temperature or humidity from 2 hours earlier';
      return <td key={'td_' + key} className={classname.trim()} title={title} style={style}>
        <span>Lag</span>
      </td>;
    }

    let formattedValue = formatNumber(data[key]);
    let modifiedValue = null;
    if (modifications && typeof modifications[key] !== 'undefined') {
      const shiftedTrField = lagHours > 0 &&
        dataSet === DATA_SETS.WEATHER &&
        (key === PREFIX_T || key === PREFIX_RH);
      if (data[key] !== modifications[key]) {
        modifiedValue = modifications[key];
      } else if (shiftedTrField) {
        modifiedValue = data[key];
      }
    }
    if (typeof data[`${key}_modified`] !== 'undefined') {
      modifiedValue = data[`${key}_modified`];
    }

    let styleValue = data[key];
    if (modifiedValue !== null) {
      const baseline = baselineData || data;
      title = `Original value: ${formatNumber(baseline[key])}`;
      classname += ' modified';
      formattedValue = formatNumber(modifiedValue);
      styleValue = modifiedValue;
    }

    // Provided tooltip overrides Modified Value tooltip.
    if (typeof data[`${key}_tooltip`] !== 'undefined') {
      title = data[`${key}_tooltip`];
    }

    if (typeof styleValue !== 'undefined') {
      if (table_directions === TABLE_DIRECTIONS.ARROWS && arrow) {
        formattedValue = <i className={`fa ${arrow}`} style={{ transform: `rotate(${formattedValue}deg)` }}/>;
      } else if (table_directions === TABLE_DIRECTIONS.CARDINALS && arrow) {
        formattedValue = toCardinal(formattedValue);
      }

      // Eliminate NaNs and Nulls
      styleValue = styleValue ? styleValue : 0;

      if ((table_background === TABLE_BACKGROUNDS.COLOR || table_background === TABLE_BACKGROUNDS.GRAPH) && colors) {
        const valid_colors = _.keys(colors.scale)
                              .sort((a, b) => Number(a) - Number(b))
                              .filter(k => Number(styleValue) < Number(k));
        const color = valid_colors.length > 0 ? colors.scale[valid_colors[0]] : colors.else;
        style = { backgroundColor: color };
      } else if (table_background === TABLE_BACKGROUNDS.GRAPH && cellGraph) {
        const g = cellGraph;
        const percentage = 100.0 * (styleValue - g.min) / (g.max - g.min);
        style =
          { 'background': `linear-gradient( to right, ${g.leftColor} 0%, ${g.leftColor} ${percentage}%, ${g.rightColor} ${percentage}%, ${g.rightColor} 100%)` };
      }
    }

    return <td key={'td_' + key} className={classname.trim()} title={title} style={style}>
      <span>{formattedValue}</span>
    </td>;
  }

  thStyle(ind) {
    if (ind.headerColor) {
      return { backgroundColor: ind.headerColor };
    }
    return {};
  }

  firstWeatherRow(weather_table_data) {
    return weather_table_data ? Object.values(weather_table_data)[0] : null;
  }
}

const mapStateToProps = state => {
  return {
    time: state.time,
    point: state.point,
    data_set: state.graphs.data_set,
    show_weather: state.graphs.show_weather,
    table_background: state.graphs.table_background,
    table_directions: state.graphs.table_directions,
    show_all_calc_columns: state.graphs.show_all_calc_columns,
    include_history: state.graphs.include_history,
    short_future: state.graphs.short_future,
    progress_percentage: state.graphs.graph_progress_percent,
    progress_seconds: state.graphs.graph_progress_seconds,
    graphs_open: state.view.graphs_open,
    graphs_loaded: state.graphs.graphs_loaded,
    graph_view_expanded: state.view.graph_view_expanded,
    app_mode: state.app.app_mode,
    pop_out: state.view.pop_out,
    print_on_load: state.view.print_on_load,
    point_data_display_graph: state.view.point_data_display_graph,
    ticks: state.point_data_weather.ticks,
    aws: state.aws.active,
    fms: state.fms.active,
    manual_weather: state.manual_weather.active,
    active_indicator: state.indicators.active,
    weather_data: state.point_data_weather,
    mcarthur_mk5_data: state.point_data_mcarthur_mk5,
    leaflet_80_data: state.point_data_leaflet_80,
    grass_data: state.point_data_grass,
    vesta_data: state.point_data_vesta,
    vesta_mk2_dry_data: state.point_data_vesta_mk2_dry,
    vesta_mk2_wet_data: state.point_data_vesta_mk2_wet,
    mallee_heath_data: state.point_data_mallee_heath,
    prescriptions_data: state.point_data_prescriptions
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch: dispatch
  };
};

PointGraphs = connect(mapStateToProps, mapDispatchToProps)(PointGraphs);

export default PointGraphs;



// WEBPACK FOOTER //
// ./src/containers/PointGraphs.js